import { createContext, useContext, useState, ReactNode } from 'react';
import { TIMEZONE, DATE_CONSTRAINTS } from '../constants';

// Helper to convert a Date to Halifax timezone for datetime-local inputs
function toHalifaxDateTimeLocal(date: Date): string {
  const halifaxString = date.toLocaleString("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  // Convert from "YYYY-MM-DD, HH:mm" to "YYYY-MM-DDTHH:mm"
  return halifaxString.replace(", ", "T");
}

interface DateRangeContextType {
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
}

const DateRangeContext = createContext<DateRangeContextType | undefined>(undefined);

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() - 48); // Default to 48 hours
    return toHalifaxDateTimeLocal(d);
  });

  const [endDate, setEndDate] = useState(() => toHalifaxDateTimeLocal(new Date()));

  return (
    <DateRangeContext.Provider value={{
      startDate,
      endDate,
      setStartDate,
      setEndDate,
    }}>
      {children}
    </DateRangeContext.Provider>
  );
}

export function useDateRange() {
  const context = useContext(DateRangeContext);
  if (!context) {
    throw new Error('useDateRange must be used within DateRangeProvider');
  }
  return context;
}

// Export utility for use elsewhere
export { toHalifaxDateTimeLocal };
